/**
 * Unified Buy Box — PDP interactive logic.
 * Handles: variant dropdown, exclusive drawers, review scroller,
 * objection modals, bundle selector, trust badge tooltips.
 */

/* --- Variant Dropdown custom element --- */
if (!customElements.get('variant-dropdown')) {
  class UbxVariantDropdown extends HTMLElement {
    constructor() {
      super();
      this.selectorSize = this.querySelector('.size-selected-js');
    }

    connectedCallback() {
      this._initListeners();
    }

    _initListeners() {
      document.addEventListener('variant:change', (e) => {
        const target =
          e.detail && e.detail.originalEvent
            ? e.detail.originalEvent.target.closest('variant-dropdown')
            : null;

        if (target !== this) return;
        this._selectedOptionSize(e.detail.originalEvent);
      });

      if (this.selectorSize) {
        this.selectorSize.addEventListener('click', (e) => {
          const dd = e.target.closest('variant-dropdown');

          if (dd) dd.classList.toggle('active');

          const handleOutside = (ev) => {
            if (!dd.contains(ev.target) && dd.classList.contains('active')) {
              dd.classList.remove('active');
              document.removeEventListener('click', handleOutside);
            }
          };

          setTimeout(() => {
            document.addEventListener('click', handleOutside);
          }, 0);
        });
      }
    }

    _selectedOptionSize(event) {
      if (!event) return;

      const parentSelector = event.target.closest('.parent-selector');
      const parentOption = event.target.closest('.size-option-js');

      if (parentOption) {
        const labelOption = parentOption.querySelector('label');
        const labelSelector = parentSelector
          ? parentSelector.querySelector('.label-selected-js')
          : null;

        if (labelSelector && labelOption)
          labelSelector.textContent = labelOption.textContent;
        if (labelOption && this.selectorSize)
          this.selectorSize.textContent = labelOption.textContent;
        this.classList.toggle('active');
      }
    }
  }

  customElements.define('variant-dropdown', UbxVariantDropdown);
}

/* --- Section-scoped interactions --- */
document.querySelectorAll('.ubx[data-section-type="product"]').forEach((section) => {
  /* --- On-image offer placement + slide-aware visibility ---
     The overlay is rendered in Liquid as the first child of the gallery
     COLUMN wrapper (.grid__item.product__photos) for simplicity. On desktop
     with thumbnail_position: beside, that wrapper includes the thumbnail
     strip, so positioning would anchor relative to the column, not the
     image. Move it inside .product__main-photos (position: relative per
     theme.css.liquid) so corner/center offsets anchor to the main image
     only. No-op on layouts without .product__main-photos (e.g., v1 slick).

     Then watch the slideshow for slide changes and hide the overlay on
     non-primary slides. Flickity adds .is-selected to the active cell;
     primary cell has data-index="0" (set in media-metafields.liquid). */
  const offer = section.querySelector('.ubx__on-image-offer');
  const mainPhotos = section.querySelector('.product__main-photos');

  // The mobile photo badges (section setting mobile_badges_on_photo) follow
  // the same contract as the offer: anchored to the main image, first slide
  // only. Each overlay keeps its own --hidden class.
  const overlays = [
    { el: offer, hiddenClass: 'ubx__on-image-offer--hidden' },
    {
      el: section.querySelector('.ubx__photo-badges'),
      hiddenClass: 'ubx__photo-badges--hidden',
    },
  ].filter((overlay) => overlay.el);

  // The feature_gallery layout renders .product__main-photos only inside a
  // display:none compatibility wrapper — moving the offer there would hide it.
  // offsetParent is null for elements inside display:none ancestors; in that
  // case leave the offer where Liquid put it (absolutely positioned over the
  // sticky .product__photos column, always visible).
  if (overlays.length && mainPhotos && mainPhotos.offsetParent !== null) {
    for (const overlay of overlays) {
      if (overlay.el.parentElement !== mainPhotos) {
        mainPhotos.appendChild(overlay.el);
      }
    }

    const slideshow = mainPhotos.querySelector('[data-product-photos]');

    if (slideshow && !slideshow.dataset.ubxOfferObs) {
      slideshow.dataset.ubxOfferObs = '1';
      const updateOfferVisibility = () => {
        const selected = slideshow.querySelector('.is-selected');
        // Before Flickity inits, no cell has .is-selected — keep visible.
        const idx = selected
          ? parseInt(selected.getAttribute('data-index') || '0', 10)
          : 0;

        for (const overlay of overlays) {
          overlay.el.classList.toggle(overlay.hiddenClass, idx !== 0);
        }
      };

      new MutationObserver(updateOfferVisibility).observe(slideshow, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      updateOfferVisibility();
    }
  }

  /* --- Hide-on-swipe for the feature_gallery (Swiper) layout ---
     The Flickity path above never runs on feature_gallery (its
     .product__main-photos is display:none), so the offer normally sits
     over every slide. Opt-in per offer block via hide_off_first_slide
     (rendered as data-ubx-offer-hide-off-first): fade the offer out
     whenever the deck is off its first slide, bring it back on slide 1 —
     same contract as the Flickity behavior, reusing the same --hidden
     class. Recursive setTimeout while waiting for the Swiper to init, not
     setInterval (the DS_06 lesson: some regional inline scripts kill
     intervals). */
  if (offer && offer.hasAttribute('data-ubx-offer-hide-off-first')) {
    const swiperDeadline = Date.now() + 10000;
    const bindOfferToSwiper = () => {
      const swiperEl = section.querySelector(
        '.feature-gallery__slider .swiper.swiper-initialized'
      );
      const swiper = swiperEl && swiperEl.swiper;
      if (!swiper) {
        if (Date.now() < swiperDeadline) {
          setTimeout(bindOfferToSwiper, 250);
        }
        return;
      }
      const updateOfferForSlide = () => {
        // realIndex ignores loop clones; the inserted video slide counts as
        // a later index, so only the true first deck slide shows the offer.
        const idx =
          typeof swiper.realIndex === 'number'
            ? swiper.realIndex
            : swiper.activeIndex;
        offer.classList.toggle('ubx__on-image-offer--hidden', idx !== 0);
      };
      swiper.on('slideChange', updateOfferForSlide);
      updateOfferForSlide();
    };
    bindOfferToSwiper();
  }

  /* --- Move social proof to attach directly to ATC ---
     Liquid renders .ubx__social-proof as a sibling of the product-form
     snippet (the form internals — ATC, Shop Pay, payment terms — are
     owned by product-form.liquid, which is shared with legacy templates
     and out of scope to modify). That puts social proof below all the
     payment elements. Fix is to DOM-move it to be the next sibling of
     the ATC button so the band visually attaches. Idempotent. */
  const socialProof = section.querySelector('.ubx__social-proof');
  const atcButton = section.querySelector('button.add-to-cart');

  if (
    socialProof &&
    atcButton &&
    socialProof.previousElementSibling !== atcButton
  ) {
    atcButton.insertAdjacentElement('afterend', socialProof);
  }

  /* --- Exclusive drawers (only one open at a time) --- */
  const drawers = section.querySelectorAll('[data-ubx-drawer]');

  drawers.forEach((drawer) => {
    const trigger = drawer.querySelector('[data-ubx-drawer-trigger]');

    if (!trigger) return;

    trigger.addEventListener('click', () => {
      const wasOpen = drawer.classList.contains('is-open');

      drawers.forEach((d) => {
        d.classList.remove('is-open');
      });

      if (!wasOpen) drawer.classList.add('is-open');
    });
  });

  /* --- Review scroller arrows --- */
  const reviewScroll = section.querySelector('[data-ubx-reviews-scroll]');

  if (reviewScroll) {
    const leftBtn = section.querySelector('[data-ubx-review-arrow="left"]');
    const rightBtn = section.querySelector('[data-ubx-review-arrow="right"]');

    const updateArrows = () => {
      if (!leftBtn || !rightBtn) return;

      leftBtn.disabled = reviewScroll.scrollLeft <= 2;
      rightBtn.disabled =
        reviewScroll.scrollLeft + reviewScroll.clientWidth >=
        reviewScroll.scrollWidth - 2;
    };

    if (leftBtn) {
      leftBtn.addEventListener('click', () => {
        const card = reviewScroll.querySelector('.ubx__review-card');
        const scrollAmt = card ? card.offsetWidth + 10 : 200;

        reviewScroll.scrollBy({ left: -scrollAmt, behavior: 'smooth' });
      });
    }

    if (rightBtn) {
      rightBtn.addEventListener('click', () => {
        const card = reviewScroll.querySelector('.ubx__review-card');
        const scrollAmt = card ? card.offsetWidth + 10 : 200;

        reviewScroll.scrollBy({ left: scrollAmt, behavior: 'smooth' });
      });
    }

    reviewScroll.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
  }

  /* --- Modal open/close with focus management --- */
  let lastFocusedEl = null;

  section.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-ubx-modal-open]');

    if (openBtn) {
      const modalId = openBtn.getAttribute('data-ubx-modal-open');
      const modal = document.getElementById(modalId);

      if (modal) {
        lastFocusedEl = openBtn;
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        const closeBtn = modal.querySelector('[data-ubx-modal-close]');

        if (closeBtn) closeBtn.focus();
      }
    }

    const closeBtn = e.target.closest('[data-ubx-modal-close]');

    if (closeBtn) {
      const overlay = closeBtn.closest('.ubx__modal-overlay');

      if (overlay) {
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';

        if (lastFocusedEl) {
          lastFocusedEl.focus();
          lastFocusedEl = null;
        }
      }
    }
  });

  /* Close modal on overlay click */
  section.querySelectorAll('.ubx__modal-overlay').forEach((overlay) => {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';

        if (lastFocusedEl) {
          lastFocusedEl.focus();
          lastFocusedEl = null;
        }
      }
    });
  });

  /* --- Bundle selector --- */
  const bundleContainer = section.querySelector('[data-ubx-bundles]');

  if (bundleContainer) {
    bundleContainer
      .querySelectorAll('input[type="radio"]')
      .forEach((radio) => {
        radio.addEventListener('change', function () {
          const variantId = this.value;

          if (!variantId) return;

          const form = section.querySelector('form.product-single__form');

          if (form) {
            const select = form.querySelector('select[name="id"]');

            if (select) {
              const option = select.querySelector(
                `option[value="${variantId}"]`
              );

              if (option) {
                select.value = variantId;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        });
      });
  }

  /* --- Tooltip toggles — tap to toggle on mobile, hover on desktop ---
     Covers any element with [data-ubx-tip-toggle]: trust badges and feature
     cards both opt in via this attribute. CSS keeps the --has-tip class for
     hover visuals; JS only needs the attribute. */
  const tips = section.querySelectorAll('[data-ubx-tip-toggle]');

  tips.forEach((btn) => {
    btn.addEventListener('click', () => {
      const wasOpen = btn.getAttribute('aria-expanded') === 'true';

      tips.forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
      });

      if (!wasOpen) btn.setAttribute('aria-expanded', 'true');
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-ubx-tip-toggle]')) {
      tips.forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* --- Bundle "what's included" caption ---
     Renders under the variant picker from the custom.bundle_includes variant
     metafield (see unified-buybox-content.liquid). The buy box swaps variants
     client-side — theme.js fires document 'variant:change' with detail.variant,
     no section re-render — so update the caption text here, keyed by variant id.
     Gated by data: hides when the selected variant has no caption. Liquid sets
     the initial line, so this only handles changes. */
  (function initBundleCaption() {
    const captionEl = section.querySelector('[data-ubx-bundle-caption]');
    const dataEl = section.querySelector('[data-ubx-bundle-caption-data]');

    if (!captionEl || !dataEl) return;

    let captionMap;

    try {
      captionMap = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const setCaption = (variantId) => {
      const text = variantId ? captionMap[variantId] : '';

      if (text) {
        captionEl.textContent = text;
        captionEl.removeAttribute('hidden');
      } else {
        captionEl.textContent = '';
        captionEl.setAttribute('hidden', '');
      }
    };

    document.addEventListener('variant:change', (e) => {
      const originalTarget =
        e.detail && e.detail.originalEvent && e.detail.originalEvent.target;

      if (!originalTarget || !section.contains(originalTarget)) return;

      const variant = e.detail && e.detail.variant;

      setCaption(variant ? variant.id : null);
    });
  })();

  /* --- Pro Upsell (snippets/ubx-pro-upsell.liquid) ---
     Original | Pro switcher + "Meet the Pro" panel. "Upgrade to Pro" turns on Pro mode:
     the size cards and pack picker keep driving theme.js as usual (it still selects
     this product's variant), and this code maps that variant to its Pro twin — the
     form's master select submits the Pro variant id, and the price, pack prices, Add to
     cart button and countdown line show the Pro. theme.js rewrites the master select,
     prices and button on every variant change, so Pro mode is re-applied after it
     (setTimeout: theme.js writes the select after dispatching variant:change). */
  (function initProUpsell() {
    const root = section.querySelector('[data-ubx-pro]');

    if (!root) return;

    const form = section.querySelector('form.product-single__form');
    const select = form && form.querySelector('select[name="id"]');
    const jsonEl = section.querySelector('[data-variant-json]');
    const modal = document.getElementById(root.dataset.ubxProModal);
    let data;
    let variants;

    try {
      data = JSON.parse(root.querySelector('[data-ubx-pro-data]').textContent);
      variants = JSON.parse(jsonEl.textContent);
    } catch (err) {
      console.error('[ubx pro] upsell disabled — data unreadable:', err);
      root.hidden = true;

      return;
    }

    if (!select || !modal) {
      console.error('[ubx pro] upsell disabled — buy box form or panel missing');
      root.hidden = true;

      return;
    }

    const { theme } = window;
    const formatMoney = (cents) =>
      theme && theme.Currency && theme.settings
        ? theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
        : `$${(cents / 100).toFixed(2)}`;
    // The Original's price as the shopper is charged it: an Intelligems price test
    // overrides Shopify's price (theme.js reads it the same way for the pack labels).
    const originalPrice = (v) => {
      const ig = window.igData && window.igData.price;
      const igPrice = ig && typeof ig.getPriceByVariantId === 'function' && ig.getPriceByVariantId(v.id);

      return igPrice || v.price;
    };
    const byId = {};

    variants.forEach((v) => {
      byId[v.id] = v;
    });

    // The master select only lists this product's variants — add the Pro's so it can
    // hold (and submit) a Pro id.
    Object.keys(data.map).forEach((origId) => {
      const opt = document.createElement('option');

      opt.value = data.map[origId].id;
      opt.textContent = `Pro - ${data.map[origId].title}`;
      select.appendChild(opt);
    });

    const tileOriginal = root.querySelector('[data-ubx-pro-original]');
    const tilePro = root.querySelector('[data-ubx-pro-tile]');
    const headingValue = root.querySelector('[data-ubx-pro-heading-value]');
    const originalLabel = headingValue ? headingValue.textContent : '';
    const priceOriginalEl = root.querySelector('[data-ubx-pro-price-original]');
    const priceProEl = root.querySelector('[data-ubx-pro-price-pro]');
    const diffEl = modal.querySelector('[data-ubx-pro-diff]');
    const atcBtn = form.querySelector('[data-add-to-cart]');
    const atcText = atcBtn && atcBtn.querySelector('[data-add-to-cart-text]');
    const priceInput = section.querySelector('#dz-updated-price');
    // Button text as the rest of the page last wrote it for the Original (theme.js,
    // then the DCOP parity shim's "Add to Cart - $129.99" a tick later — both run
    // before render). Captured on entering Pro mode and on each variant change in it;
    // restored when leaving.
    let originalAtcText = '';
    const promoEls = data.promoContent
      ? Array.from(section.querySelectorAll('.promotional-section__content p'))
      : [];

    promoEls.forEach((p) => {
      p.dataset.ubxProSavedText = p.textContent;
    });

    const titleEl = section.querySelector('.product-single__title');
    const originalTitle = titleEl ? titleEl.textContent : '';

    let proMode = false;
    let currentId = Number(select.value);
    const reportedMissing = {};

    // Tiles always follow the selected variant. The buy box itself (master select,
    // prices, button, countdown) is only touched in Pro mode or when leaving it, so
    // with the Original selected theme.js stays its sole owner, exactly as today.
    const render = (syncBuyBox) => {
      const orig = byId[currentId];

      if (!orig) return;

      const pro = data.map[currentId];

      if (!pro && !reportedMissing[currentId]) {
        reportedMissing[currentId] = true;
        console.error(
          `[ubx pro] no Pro variant matches "${orig.title}" — check the Pro's Height/Bundle option values`
        );
      }

      section.classList.toggle('ubx--pro-mode', proMode);
      tileOriginal.setAttribute('aria-pressed', String(!proMode));
      tilePro.setAttribute('aria-pressed', String(proMode));
      if (headingValue) headingValue.textContent = proMode ? 'Pro' : originalLabel;
      priceOriginalEl.innerHTML = formatMoney(originalPrice(orig));
      priceProEl.innerHTML = pro ? formatMoney(pro.price) : '';

      if (diffEl) {
        diffEl.hidden = !pro || pro.price <= orig.price;
        if (!diffEl.hidden) {
          const amount = formatMoney(pro.price - orig.price)
            .replace(/<[^>]*>/g, '')
            .trim()
            .replace(/\.00$/, '');

          diffEl.textContent = diffEl.dataset.template.replace('[amount]', amount);
        }
      }

      if (!proMode && !syncBuyBox) return;

      const shown = proMode && pro ? pro : orig;

      if (atcText && proMode && syncBuyBox !== true) {
        originalAtcText = atcText.textContent;
      }

      select.value = String(shown.id);

      // Main price (+ compare-at, when the template renders one).
      const priceEl = section.querySelector('[data-product-price]');
      const compareWrap = section.querySelector('[data-product-price-wrap]');
      const compareEl = section.querySelector('[data-compare-price]');

      if (priceEl) priceEl.innerHTML = formatMoney(shown === orig ? originalPrice(orig) : shown.price);
      if (compareWrap && compareEl) {
        const onSale = shown.compare_at_price > shown.price;

        compareWrap.classList.toggle('hide', !onSale);
        compareEl.innerHTML = onSale ? formatMoney(shown.compare_at_price) : '';
      }

      // Pack picker prices: each label names its Bundle value; resolve it at the
      // selected Height.
      section.querySelectorAll('.price-bundle-js[data-value-bundle]').forEach((el) => {
        const v = variants.find(
          (x) => x.option1 === orig.option1 && x.option2 === el.dataset.valueBundle
        );

        if (!v) return;

        const p = data.map[v.id];

        el.innerHTML = formatMoney(proMode && p ? p.price : originalPrice(v));
      });

      if (atcBtn && atcText) {
        if (proMode) {
          const available = Boolean(pro && pro.available);

          atcBtn.disabled = !available;
          atcBtn.classList.toggle('disabled', !available);
          atcText.textContent = available
            ? `${data.atcLabel} - ${formatMoney(pro.price).replace(/<[^>]*>/g, '').trim()}`
            : (theme && theme.strings && theme.strings.soldOut) || 'Sold out';
        } else {
          atcBtn.disabled = !orig.available;
          atcBtn.classList.toggle('disabled', !orig.available);
          atcText.textContent = originalAtcText;
        }
      }

      // Raw price of what Add to cart will add (mirrored by the DCOP parity shim for
      // consumers outside the theme).
      if (priceInput) priceInput.value = shown.price / 100;

      promoEls.forEach((p) => {
        p.textContent = proMode ? data.promoContent : p.dataset.ubxProSavedText;
      });

      if (titleEl && data.title) titleEl.textContent = proMode ? data.title : originalTitle;
    };

    const setMode = (on) => {
      if (on === proMode) return;
      if (on && atcText) originalAtcText = atcText.textContent;
      proMode = on;
      render(true);
    };

    // trigger gets focus back when the panel closes (the section's modal handler).
    const openPanel = (trigger) => {
      lastFocusedEl = trigger;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      const closeBtn = modal.querySelector('[data-ubx-modal-close]');

      if (closeBtn) closeBtn.focus();
    };

    // Tapping the Pro tile selects the Pro outright (closing the panel keeps it). The
    // panel also opens on that tap, but only the first PANEL_AUTO_OPENS times in a
    // browsing session; after that the tiles just switch, and "What's the difference?"
    // always opens it. No storage (private mode) = no limit tracking = always opens.
    const PANEL_AUTO_OPENS = 2;
    const AUTO_OPEN_KEY = 'ubxProPanelAutoOpens';
    const takeAutoOpen = () => {
      try {
        const n = Number(sessionStorage.getItem(AUTO_OPEN_KEY)) || 0;

        if (n >= PANEL_AUTO_OPENS) return false;
        sessionStorage.setItem(AUTO_OPEN_KEY, String(n + 1));
      } catch (err) {
        // storage blocked — fall through and open
      }

      return true;
    };

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-ubx-pro-original]')) {
        setMode(false);
      } else if (e.target.closest('[data-ubx-pro-tile]')) {
        if (proMode) return;
        setMode(true);
        if (takeAutoOpen()) openPanel(tilePro);
      } else if (e.target.closest('[data-ubx-pro-info]')) {
        openPanel(e.target.closest('[data-ubx-pro-info]'));
      }
    });

    // Swipeable photos in the panel content: scroll-snap does the swiping; this adds
    // the dots and the desktop arrows and keeps them in step with the scroll position.
    modal.querySelectorAll('[data-ubx-pro-slides]').forEach((gallery) => {
      const track = gallery.querySelector('.ubx-pro__slides');
      const slides = track ? Array.from(track.children) : [];

      if (slides.length < 2) return;

      const goTo = (i) => {
        const target = slides[Math.max(0, Math.min(slides.length - 1, i))];

        track.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      };
      const dotsEl = document.createElement('div');

      dotsEl.className = 'ubx-pro__dots';
      const dots = slides.map((_, i) => {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'ubx-pro__dot';
        dot.setAttribute('aria-label', `Photo ${i + 1} of ${slides.length}`);
        dot.addEventListener('click', () => goTo(i));
        dotsEl.appendChild(dot);

        return dot;
      });
      const makeArrow = (dir) => {
        const btn = document.createElement('button');

        btn.type = 'button';
        btn.className = `ubx-pro__arrow ubx-pro__arrow--${dir}`;
        btn.setAttribute('aria-label', dir === 'prev' ? 'Previous photo' : 'Next photo');
        btn.textContent = dir === 'prev' ? '‹' : '›';
        gallery.appendChild(btn);

        return btn;
      };
      const prev = makeArrow('prev');
      const next = makeArrow('next');
      let current = 0;

      const update = () => {
        const step = slides[1].offsetLeft - slides[0].offsetLeft;

        // step is 0 while the panel is closed (display: none) — stay on the first photo.
        current = step > 0 ? Math.round(track.scrollLeft / step) : 0;
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === current)));
        prev.disabled = current === 0;
        next.disabled = current >= slides.length - 1;
      };

      prev.addEventListener('click', () => goTo(current - 1));
      next.addEventListener('click', () => goTo(current + 1));
      track.addEventListener('scroll', update, { passive: true });
      gallery.appendChild(dotsEl);
      update();
    });

    // The panel's buttons also carry data-ubx-modal-close, so the section's modal
    // handler closes it; these only set the mode.
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-ubx-pro-choose]')) setMode(true);
      else if (e.target.closest('[data-ubx-pro-keep]')) setMode(false);
    });

    document.addEventListener('variant:change', (e) => {
      const originalTarget =
        e.detail && e.detail.originalEvent && e.detail.originalEvent.target;

      if (!originalTarget || !section.contains(originalTarget)) return;

      const variant = e.detail && e.detail.variant;

      if (!variant) return;

      currentId = variant.id;
      setTimeout(render, 0);
    });

    render();
  })();

  /* --- Variant chips (picker style "chips") ---
     Liquid renders each chip's price / savings / sold-out state for the
     combination selected on load. Two things need JS:
       1. the block's "selected on load" value (data-ubx-chips-default) is
          applied by clicking that chip's radio — the click runs every
          theme.js listener (price, master select, images) exactly like a
          shopper's tap. Skipped when the URL carries ?variant= (deep links
          win) or the value is sold out. theme.js binds its change listeners
          when it initializes the product section, which may land after this
          script — so the selection is re-asserted once the form's master
          select proves it did not take.
       2. on products with more than one option, a change in ANOTHER option
          (e.g. Color) changes which variant each chip resolves to, so price,
          savings and availability are recomputed from the variants JSON. */
  (function initChips() {
    const groups = Array.from(section.querySelectorAll('[data-ubx-chips]'));

    if (!groups.length) return;

    const jsonEl = section.querySelector('[data-variant-json]');
    let variants = [];

    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      variants = [];
    }

    // theme.settings.moneyFormat is the store's HTML money format
    // (<span class=money>…</span>); the chips are written with textContent,
    // so strip the markup and keep the figure.
    const formatMoney = (cents) => {
      const raw =
        window.theme && theme.Currency && theme.settings
          ? theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
          : `$${(cents / 100).toFixed(2)}`;

      return String(raw).replace(/<[^>]*>/g, '').trim();
    };

    // Free gift whose price counts toward the savings % (snippets/free-gift-attr).
    // Same formula as snippets/free-gift-saving.liquid and theme.js.
    const freeGift = section.dataset.freeGift
      ? JSON.parse(section.dataset.freeGift)
      : null;
    const freeGiftSaving = (variant, price) => {
      // Map of variant id -> total value of the gifts that come with it.
      const gift = freeGift[variant.id] || 0;
      const base = Math.max(variant.compare_at_price || 0, price);
      const saving = base - price + gift;

      return saving > 0 ? `${Math.round((saving * 100) / (base + gift))}%` : '';
    };

    const setText = (el, text) => {
      if (el && el.textContent.trim() !== text) el.textContent = text;
    };

    const checkedOptions = () => {
      const selected = {};

      for (const input of section.querySelectorAll('[data-variant-input]')) {
        if (input.checked) selected[input.dataset.index] = input.value;
      }

      return selected;
    };

    const refresh = () => {
      const selected = checkedOptions();

      for (const group of groups) {
        const optionKey = group.dataset.ubxChipsOption;

        for (const chip of group.querySelectorAll('[data-ubx-chip]')) {
          const value = chip.dataset.value;
          const variant = variants.find((candidate) => {
            if (candidate[optionKey] !== value) return false;

            return Object.keys(selected).every(
              (key) => key === optionKey || candidate[key] === selected[key]
            );
          });
          const available = Boolean(variant && variant.available);
          // Intelligems price tests rewrite prices client-side; resolve the
          // chip price the way theme.js and the DS_10 sheet do, so the chips
          // agree with the price block / ATC under a live test.
          const price = variant
            ? window.igData?.price?.getPriceByVariantId(variant.id) ||
              variant.price
            : 0;
          const saving =
            variant && variant.compare_at_price > price
              ? variant.compare_at_price - price
              : 0;
          const input = chip.querySelector('input[data-variant-input]');
          const label = chip.querySelector('label');
          const tag = chip.querySelector('[data-ubx-chip-tag]');

          chip.classList.toggle('ubx__chip--oos', !available);
          if (input) input.classList.toggle('disabled', !available);
          if (label) label.classList.toggle('disabled', !available);
          setText(
            chip.querySelector('[data-ubx-chip-price]'),
            variant ? formatMoney(price) : ''
          );

          let tagText = '';

          if (!available) tagText = 'Sold out';
          else if (freeGift) {
            const giftSaving = variant ? freeGiftSaving(variant, price) : '';

            if (giftSaving) tagText = `Save ${giftSaving}`;
          } else if (saving > 0) {
            tagText = `Save ${formatMoney(saving).replace(/\.00$/, '')}`;
          }
          setText(tag, tagText);
          if (tag) tag.classList.toggle('ubx__chip-tag--on', tagText !== '');
        }
      }
    };

    // The default selection is not a shopper's tap: the page should still
    // land on the hero photo (theme.js swaps the gallery to the selected
    // variant's image on every change). Put the gallery back on its first
    // slide until the shopper interacts.
    let shopperTouched = false;

    for (const type of ['pointerdown', 'touchstart', 'keydown']) {
      section.addEventListener(type, () => { shopperTouched = true; }, {
        passive: true,
        once: true,
      });
    }

    const goToFirstSlide = () => {
      if (!window.Flickity) return;

      for (const slideshow of section.querySelectorAll('[data-product-photos]')) {
        const flickity = window.Flickity.data(slideshow);

        if (flickity && flickity.selectedIndex !== 0) {
          flickity.select(0, false, true);
        }
      }
    };

    const showFirstSlide = () => {
      if (!shopperTouched) goToFirstSlide();
    };

    const applyDefault = () => {
      if (/[?&]variant=/.test(window.location.search)) return;

      for (const group of groups) {
        const value = group.dataset.ubxChipsDefault;

        if (!value) continue;

        const input = Array.from(
          group.querySelectorAll('input[data-variant-input]')
        ).find((candidate) => candidate.value === value);

        if (!input || input.classList.contains('disabled')) continue;

        if (!input.checked) {
          input.click();
        }

        if (!input.checked) {
          input.checked = true;
        }

        // theme.js listens for change on the radio itself; if its product
        // section had not initialized when the click ran, the master select
        // still points at the old variant — fire change again so it catches up.
        const masterSelect = section.querySelector('select[data-product-select]');
        const settle = () => {
          const selected = checkedOptions();
          const target = variants.find((candidate) =>
            Object.keys(selected).every(
              (key) => candidate[key] === selected[key]
            )
          );

          if (
            masterSelect &&
            target &&
            String(masterSelect.value) !== String(target.id)
          ) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }

          showFirstSlide();
        };

        showFirstSlide();
        setTimeout(settle, 400);
        setTimeout(settle, 1500);
      }
    };

    document.addEventListener('variant:change', (e) => {
      const originalTarget =
        e.detail && e.detail.originalEvent && e.detail.originalEvent.target;

      if (!originalTarget || !section.contains(originalTarget)) return;
      refresh();

      // A value without a photo of its own (the eye mask's 1-Pack / 2-Pack)
      // would otherwise keep showing the previous pack's bundle photo —
      // theme.js only swaps the gallery when the variant HAS an image. Send
      // it back to the hero. After theme.js's own change handlers (this
      // event fires before its _updateImages runs).
      const variant = e.detail && e.detail.variant;

      if (variant && !variant.featured_image && !variant.featured_media) {
        setTimeout(goToFirstSlide, 0);
      }
    });

    const start = () => {
      applyDefault();
      refresh();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  })();

  /* --- Color swatch OOS re-evaluation per selected size --- */
  // theme.js's setAvailability() marks a color .variant-oos if ANY size of
  // that color is sold out. Re-stamp the class so it only strikes through
  // when (selected size + that color) is actually unavailable.
  (function initSwatchOOS() {
    const jsonEl = section.querySelector('textarea[data-variant-json]');

    if (!jsonEl) return;

    let variants;

    try {
      variants = JSON.parse(jsonEl.value);
    } catch (e) {
      return;
    }

    if (!Array.isArray(variants) || variants.length === 0) return;

    const variantControl = section.querySelector(
      '.product-block--variant-selector'
    );

    if (!variantControl) return;

    const fieldsets = Array.from(
      variantControl.querySelectorAll('fieldset[data-index]')
    );

    if (fieldsets.length < 2) return;

    const colorFieldset = fieldsets.find((fs) =>
      fs.querySelector('input.variant__input--color-swatch')
    );

    if (!colorFieldset) return;

    const colorKey = colorFieldset.dataset.index;
    const otherFieldsets = fieldsets.filter((fs) => fs !== colorFieldset);

    const reevaluate = () => {
      const selectedOther = {};

      otherFieldsets.forEach((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');

        if (checked) selectedOther[fs.dataset.index] = checked.value;
      });

      const colorInputs = colorFieldset.querySelectorAll(
        'input.variant__input--color-swatch'
      );

      colorInputs.forEach((input) => {
        const label = input.parentElement.querySelector('label.color-swatch');

        if (!label) return;

        const match = variants.find((v) => {
          if (v[colorKey] !== input.value) return false;

          return Object.keys(selectedOther).every(
            (k) => v[k] === selectedOther[k]
          );
        });

        const shouldStrike = !match || match.available === false;

        label.classList.toggle('variant-oos', shouldStrike);
      });
    };

    document.addEventListener('variant:change', (e) => {
      const originalTarget =
        e.detail && e.detail.originalEvent && e.detail.originalEvent.target;

      if (!originalTarget || !section.contains(originalTarget)) return;

      reevaluate();
    });

    const runInitial = () => setTimeout(reevaluate, 0);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runInitial, { once: true });
    } else {
      runInitial();
    }

    window.addEventListener('load', reevaluate, { once: true });
  })();

  /* --- Color image filter (opt-in via enable_color_image_filter section setting) --- */
  // Ported in parallel from src/js/PDP/change-imagen-variant.js — kept separate by
  // design so the sheets-revamp bundle stays untouched. Filters slides whose alt
  // text contains the selected color wrapped in literal backslashes (\white\).
  (function initColorImageFilter() {
    try {
      // Opt-in check FIRST — bail out before any DOM work if the section toggle is off
      const sectionId = section.dataset.sectionId;
      if (
        !sectionId ||
        !window._ubxColorFilter ||
        window._ubxColorFilter[sectionId] !== true
      ) {
        return;
      }

      // Layout guard — this filter rebuilds the gallery as a Flickity carousel, so it
      // only works on the layouts that already ARE a Flickity carousel of
      // .product-main-slide cells: 'v2_layout' (product-images) and 'v3_layout'
      // (product-images-grid-slider). Both emit the same [data-product-photos] /
      // [data-product-thumbs] structure this code rebuilds; v3's desktop grid is pure
      // CSS layered on the same Flickity, so filtering leaves the layout intact.
      // The Slick (v1) layout has a different structure, so skip and warn loudly.
      const SUPPORTED_LAYOUTS = ['v2_layout', 'v3_layout'];
      const layout =
        window._ubxColorFilterLayout && window._ubxColorFilterLayout[sectionId];
      if (SUPPORTED_LAYOUTS.indexOf(layout) === -1) {
        console.warn(
          'UBX color filter: enabled but gallery layout is "' +
            layout +
            '", not one of ' +
            SUPPORTED_LAYOUTS.join('/') +
            ' (Flickity) — skipping so the gallery is left unchanged.'
        );
        return;
      }

      const main = section.querySelector('[data-product-photos]');

      if (!main) return;

      const slideSelector = '.product-main-slide';
      const thumbItemSelector = '.product__thumb-item';
      const thumbsWrap = section.querySelector('[data-product-thumbs]');

      const allSlidesHTML = Array.from(main.querySelectorAll(slideSelector)).map(
        (s) => s.outerHTML
      );
      const allThumbsHTML = thumbsWrap
        ? Array.from(thumbsWrap.querySelectorAll(thumbItemSelector)).map(
            (t) => t.outerHTML
          )
        : [];

      if (allSlidesHTML.length === 0) return;

      // The gallery markup carries the theme's own Flickity config (v3 sets
      // dragThreshold: 80). Rebuilding drops it, so re-apply it over this feature's
      // defaults — otherwise the carousel swipes at the theme's sensitivity on load
      // and at this feature's after the first color change. v1/v2 set no attribute
      // and are unaffected.
      let themeFlickityOptions = {};

      try {
        themeFlickityOptions = JSON.parse(main.getAttribute('data-flickity') || '{}');
      } catch (attrErr) {
        console.warn(
          'UBX color filter: could not parse the gallery\'s data-flickity attribute, ' +
            'falling back to this feature\'s defaults:',
          attrErr
        );
      }

      let flickityInstance = null;

      function getSelectedColor() {
        const checked = section.querySelector(
          'input[type=radio].variant__input--color-swatch:checked'
        );

        return checked ? checked.value.toLowerCase().trim() : '';
      }

      // Matches the color name wrapped in literal backslashes (\white\) anywhere in the
      // slide's markup — in practice, the media's alt text.
      function colorPattern(color) {
        const escaped = color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        return new RegExp('\\\\\\s*' + escaped + '\\s*\\\\', 'i');
      }

      // Every color this product offers, read off the swatches themselves so the tag
      // vocabulary can't drift from the option values.
      const allColors = Array.from(
        section.querySelectorAll('input[type=radio].variant__input--color-swatch')
      )
        .map((input) => (input.value || '').toLowerCase().trim())
        .filter(Boolean);

      const colorPatterns = allColors.map(colorPattern);

      // Media tagged with no color at all (videos, size charts, lifestyle shots) is
      // universal — it stays in the gallery whichever swatch is selected.
      function isColorTagged(html) {
        return colorPatterns.some((pat) => pat.test(html));
      }

      // Rebuilding the carousel makes the page jump on mobile: the fresh
      // Flickity viewport starts at 0px and animates up to the slide height
      // (its adaptiveHeight transition), so for ~200ms the document is a whole
      // photo shorter, the browser drops the scroll position to match and then
      // restores it — a visible jump on every color tap (measured on the sheets
      // PDP: scroll 1206 → 816 → 1206, gallery 420 → 30 → 420px). Pin the
      // container's current height across the rebuild and release it once the
      // new carousel has settled. Each mount owns its own release so a fast
      // second tap can't unpin a rebuild that is still in flight.
      let mountToken = 0;

      function mount(slidesHTML, thumbsHTML) {
        const heldHeight = main.offsetHeight;
        const token = ++mountToken;

        if (heldHeight > 0) main.style.minHeight = heldHeight + 'px';

        const existingF = window.Flickity ? window.Flickity.data(main) : null;

        if (existingF) existingF.destroy();

        main.innerHTML = slidesHTML.join('');

        setTimeout(() => {
          if (token === mountToken) main.style.minHeight = '';
        }, 400);

        const slides = Array.from(main.querySelectorAll(slideSelector));

        slides.forEach((slide, i) => {
          slide.setAttribute('data-index', i);
          slide.setAttribute('tabindex', i === 0 ? '0' : '-1');
          slide.classList.toggle('starting-slide', i === 0);
          slide.classList.remove('is-selected');
        });

        if (thumbsWrap && thumbsHTML.length) {
          thumbsWrap.innerHTML =
            '<div class="product__thumbs--scroller">' +
            thumbsHTML.join('') +
            '</div>';

          const thumbItems = Array.from(
            thumbsWrap.querySelectorAll(thumbItemSelector)
          );

          thumbItems.forEach((t, i) => {
            t.setAttribute('data-index', i);

            const a = t.querySelector('[data-product-thumb]');

            if (a) {
              a.classList.toggle('is-active', i === 0);
              a.setAttribute('data-index', i);
            }
          });

          thumbsWrap.addEventListener('click', (evt) => {
            const item = evt.target.closest(thumbItemSelector);

            if (!item) return;

            const idx = parseInt(item.getAttribute('data-index'), 10);

            if (!Number.isNaN(idx) && flickityInstance) {
              flickityInstance.select(idx, false, true);
              thumbsWrap
                .querySelectorAll('[data-product-thumb]')
                .forEach((a) => a.classList.remove('is-active'));

              const a = item.querySelector('[data-product-thumb]');

              if (a) a.classList.add('is-active');
              evt.preventDefault();
            }
          });
        }

        if (window.Flickity) {
          flickityInstance = new window.Flickity(
            main,
            Object.assign(
              {
                cellSelector: slideSelector,
                wrapAround: true,
                pageDots: true,
                prevNextButtons: true,
                adaptiveHeight: true,
                contain: true,
                cellAlign: 'center',
                dragThreshold: 3
              },
              themeFlickityOptions
            )
          );

          if (thumbsWrap) {
            flickityInstance.on('change', (index) => {
              thumbsWrap
                .querySelectorAll('[data-product-thumb]')
                .forEach((a) => a.classList.remove('is-active'));

              const active = thumbsWrap.querySelector(
                thumbItemSelector +
                  '[data-index="' +
                  index +
                  '"] [data-product-thumb]'
              );

              if (active) active.classList.add('is-active');
              slides.forEach((s, i) =>
                s.setAttribute('tabindex', i === index ? '0' : '-1')
              );
            });
          }
        }

        try {
          const imageContainer = main.closest('[data-product-images]');

          if (imageContainer && window.theme && window.theme.Photoswipe) {
            new window.theme.Photoswipe(
              imageContainer,
              imageContainer.dataset.sectionId || ''
            );
          }
        } catch (psErr) {
          console.warn('UBX color filter: Photoswipe re-init skipped:', psErr);
        }

        // Desktop click-to-zoom — the innerHTML rebuild drops the per-slide zoom
        // handlers, so re-attach them (ported from change-imagen-variant.js).
        if (window.matchMedia('(min-width: 768px)').matches) {
          const zoomSlides = main.querySelectorAll(slideSelector);
          let isZooming = false;

          zoomSlides.forEach((slideEl) => {
            slideEl.classList.remove('is-selected');

            const btn = slideEl.querySelector('.image-wrap');

            if (!btn) return;

            btn.addEventListener('click', (evt) => {
              if (isZooming) return;
              isZooming = true;

              const clicked = evt.target.closest(slideSelector);

              if (clicked) {
                zoomSlides.forEach((s) => s.classList.remove('is-selected'));
                clicked.classList.add('is-selected');

                setTimeout(() => {
                  const zoomBtn = clicked.querySelector('.js-photoswipe__zoom');

                  if (zoomBtn) zoomBtn.click();
                  setTimeout(() => (isZooming = false), 400);
                }, 100);
              }
            });
          });
        }
      }

      function applyColor(color) {
        if (!color) return;

        const pat = colorPattern(color);

        // Nothing tagged for this color — show the whole gallery rather than an empty
        // one, and say why, since the cause is always missing alt text in admin.
        if (!allSlidesHTML.some((html) => pat.test(html))) {
          console.warn(
            'UBX color filter: no gallery media tagged "\\' +
              color +
              '\\" — showing all media unfiltered. Add \\' +
              color +
              '\\ to that color\'s image alt text in Shopify admin.'
          );
          mount(allSlidesHTML, allThumbsHTML);

          return;
        }

        // Keep this color's media plus everything untagged, in the original order.
        const keep = (html) => pat.test(html) || !isColorTagged(html);

        mount(allSlidesHTML.filter(keep), allThumbsHTML.filter(keep));
      }

      // Bail BEFORE touching the gallery if there is nothing to filter by. mount()
      // replaces the theme's own slideshow with this feature's Flickity instance
      // (different page dots, arrows and drag behaviour), so rebuilding a gallery we
      // then show in full is a visible change for no benefit. Leave it exactly as the
      // theme built it and say why — the tagging lives in Shopify admin, not git, so an
      // untagged product is otherwise indistinguishable from a broken filter.
      if (!allColors.length) {
        console.warn(
          'UBX color filter: enabled, but this product has no color swatches — ' +
            'nothing to filter by. Leaving the gallery untouched.'
        );

        return;
      }

      if (!allSlidesHTML.some(isColorTagged)) {
        console.warn(
          'UBX color filter: enabled, but no gallery media carries a \\color\\ tag in ' +
            'its alt text (expected one of: ' +
            allColors.join(', ') +
            '). Leaving the gallery untouched — set the alt text in Shopify admin to ' +
            'turn filtering on.'
        );

        return;
      }

      const initialColor = getSelectedColor();

      // Filter straight to the selected color rather than mounting everything first —
      // one rebuild on load instead of two.
      if (initialColor) {
        applyColor(initialColor);
      } else {
        mount(allSlidesHTML, allThumbsHTML);
      }

      // Re-filter on variant change — scoped to this section's color swatches
      document.addEventListener('variant:change', (e) => {
        try {
          const target =
            e.detail && e.detail.originalEvent && e.detail.originalEvent.target;

          if (!target || !section.contains(target)) return;
          if (target.name !== 'Color') return;

          const color = (target.value || '').toLowerCase().trim();

          if (color) applyColor(color);
        } catch (evErr) {
          console.warn(
            'UBX color filter: variant:change handler error:',
            evErr
          );
        }
      });
    } catch (err) {
      console.warn('UBX color image filter: init failed:', err);
    }
  })();
});

/* ========== THIRD-PARTY TESTS BLOCK ========== */

function initThirdPartyTests(section) {
  const trigger = section.querySelector('[data-ubx-tpt-trigger]');

  if (!trigger) return;

  const modal = section.querySelector('[data-ubx-tpt-modal]');
  const dialog = modal && modal.querySelector('[data-ubx-tpt-dialog]');
  const closeBtn = dialog && dialog.querySelector('[data-ubx-tpt-close]');
  const carousel = dialog && dialog.querySelector('[data-ubx-tpt-carousel]');
  const cards = dialog
    ? Array.from(dialog.querySelectorAll('[data-ubx-tpt-card]'))
    : [];

  if (!modal || !dialog || !closeBtn || !carousel || cards.length === 0) return;

  const dots = Array.from(dialog.querySelectorAll('[data-ubx-tpt-dot]'));
  const prevBtn = dialog.querySelector('[data-ubx-tpt-prev]');
  const nextBtn = dialog.querySelector('[data-ubx-tpt-next]');

  let activeIndex = 0;
  let lastFocused = null;

  function getFocusable() {
    return Array.from(
      dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      const card = el.closest('[data-ubx-tpt-card]');

      return !card || card.classList.contains('is-active');
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;

    const focusable = getFocusable();

    if (!focusable.length) {
      e.preventDefault();

      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function handleKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();

      return;
    }

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const inCarousel =
        carousel.contains(document.activeElement) ||
        document.activeElement === carousel;

      if (!inCarousel) return;

      e.preventDefault();

      if (e.key === 'ArrowRight') {
        goToCard(Math.min(activeIndex + 1, cards.length - 1));
      } else {
        goToCard(Math.max(activeIndex - 1, 0));
      }
    }
  }

  function goToCard(idx) {
    if (idx < 0 || idx >= cards.length) return;

    activeIndex = idx;

    cards.forEach((card, i) => {
      const isActive = i === idx;

      card.classList.toggle('is-active', isActive);

      if (isActive) card.removeAttribute('aria-hidden');
      else card.setAttribute('aria-hidden', 'true');
    });

    dots.forEach((dot, i) => {
      const isActive = i === idx;

      dot.classList.toggle('is-active', isActive);

      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx === cards.length - 1;
  }

  function openModal() {
    if (modal.classList.contains('is-open')) return;

    lastFocused = document.activeElement;
    modal.classList.add('is-open');
    document.documentElement.classList.add('ubx-tpt-locked');
    modal.addEventListener('keydown', trapFocus);
    document.addEventListener('keydown', handleKey);

    setTimeout(() => closeBtn.focus(), 50);
  }

  function closeModal() {
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('ubx-tpt-locked');
    modal.removeEventListener('keydown', trapFocus);
    document.removeEventListener('keydown', handleKey);

    if (lastFocused) {
      lastFocused.focus();
      lastFocused = null;
    }
  }

  trigger.addEventListener('click', openModal);
  closeBtn.addEventListener('click', closeModal);

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', () => goToCard(activeIndex - 1));
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => goToCard(activeIndex + 1));
  }

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => goToCard(i));
  });

  // Reveal trigger only after wiring is complete — JS-load failure leaves no
  // broken click target.
  trigger.removeAttribute('hidden');
}

document
  .querySelectorAll('.ubx[data-section-type="product"]')
  .forEach((section) => {
    initThirdPartyTests(section);
  });
